import { writable } from 'svelte/store';
import { migrateIslandTriggerGatesInScene } from 'src/components/Flows/converters/flow-island-utils';

function normalize_scene_ids(scenes_obj: Record<string, any>): Record<string, any> {
	const result = { ...scenes_obj };
	for (const [key, value] of Object.entries(result)) {
		if (value.id === undefined) {
			result[key] = { ...value, id: key };
		}
	}
	return result;
}

function isFlowSceneForCache(scene: Record<string, any>): boolean {
	const meta = scene._meta;
	return meta?.sceneType === 'flow' || meta?.isFlow === true || !!meta?.flowGraph;
}

function migrateFlowSceneIfNeeded(scene: Record<string, any>): Record<string, any> {
	if (!scene || typeof scene !== 'object' || !isFlowSceneForCache(scene)) return scene;
	return migrateIslandTriggerGatesInScene(scene);
}

function migrateFlowScenesRecord(scenes_obj: Record<string, any>): Record<string, any> {
	const out: Record<string, any> = {};
	for (const [key, scene] of Object.entries(scenes_obj)) {
		out[key] = migrateFlowSceneIfNeeded(scene);
	}
	return out;
}

/** Drop flow canvas layout from localStorage for non-flow scenes; keep flowGraph for flows so refresh can restore layout before API returns. */
function stripFlowGraphForLocalCache(scenes_obj: Record<string, any>): Record<string, any> {
	const out: Record<string, any> = {};
	for (const [key, scene] of Object.entries(scenes_obj)) {
		if (!scene || typeof scene !== 'object') {
			out[key] = scene;
			continue;
		}
		if (isFlowSceneForCache(scene)) {
			out[key] = scene;
			continue;
		}
		const meta = scene._meta;
		if (meta && typeof meta === 'object' && 'flowGraph' in meta) {
			const { flowGraph: _omit, ...restMeta } = meta as Record<string, unknown>;
			out[key] = { ...scene, _meta: restMeta };
		} else {
			out[key] = scene;
		}
	}
	return out;
}

function create_scenes() {
	const initialScenes = normalize_scene_ids(JSON.parse(localStorage.getItem('scenes')) || {});
	const { subscribe, set, update } = writable(initialScenes);

	function mergePreservedFlowGraph(
		incoming: Record<string, any>,
		previous: Record<string, any>,
	): Record<string, any> {
		const out = { ...incoming };
		for (const [key, scene] of Object.entries(out)) {
			if (!scene || typeof scene !== 'object') continue;
			const prevFg = previous[key]?._meta?.flowGraph;
			if (prevFg && scene._meta && !scene._meta.flowGraph) {
				out[key] = { ...scene, _meta: { ...scene._meta, flowGraph: prevFg } };
			}
		}
		return out;
	}

	return {
		subscribe,
		set: (scenes) => {
			update((current) =>
				mergePreservedFlowGraph(migrateFlowScenesRecord(normalize_scene_ids(scenes)), current),
			);
		},
		update: (_scenes) =>
			update((scenes) => {
				// Mainly used to handle list edits for scenes from the socket event apiToUi
				// On scene add we recieve only the scene object and must add _id prop to it.
				// On edit e recieve the whole scenes list object with _id prop already present.
				if (Object.keys(_scenes).length === 1) {
					console.log('Add one scene from socket APItoUI');
					let newId = Object.keys(_scenes)[0].toString();
					const incomingScene = Object.values(_scenes)[0];
					const existingScene = scenes[newId];

					const merged = {
						//@ts-ignore
						...incomingScene,
						_id: newId,
						id: newId,
					};

					// Preserve frontend-only _meta.new flag for pulse animation
					// Prevents socket updates from wiping pulse animation state
					if (existingScene?._meta?.new && merged?._meta && !merged?._meta?.new) {
						merged._meta = { ...merged._meta, new: existingScene._meta.new };
					}

					// API/socket payloads often omit editor-only flowGraph — keep local layout topology.
					const existingFlowGraph = existingScene?._meta?.flowGraph;
					if (existingFlowGraph && merged?._meta && !merged._meta.flowGraph) {
						merged._meta = { ...merged._meta, flowGraph: existingFlowGraph };
					}

					return { ...scenes, [newId]: migrateFlowSceneIfNeeded(merged) };
				}

				// In the context of a sub-account, retain the rights value when the scene is changed in the master account.
				for (const [key, value] of Object.entries(_scenes)) {
					const oldScene = scenes?.[key];
					if (typeof oldScene?._meta?.rights !== 'undefined') {
						(value as any)._meta.rights = oldScene._meta.rights;
					}
					// Preserve frontend-only _meta.new flag for pulse animation
					if (oldScene?._meta?.new && (value as any)?._meta && !(value as any)._meta.new) {
						(value as any)._meta.new = oldScene._meta.new;
					}
					const oldFlowGraph = oldScene?._meta?.flowGraph;
					if (oldFlowGraph && (value as any)?._meta && !(value as any)._meta.flowGraph) {
						(value as any)._meta = { ...(value as any)._meta, flowGraph: oldFlowGraph };
					}
					_scenes[key] = migrateFlowSceneIfNeeded(value as Record<string, any>);
				}
				return { ...scenes, ...normalize_scene_ids(_scenes) };
			}),
		clearNewFlag: (id) =>
			update((scenes) => {
				if (scenes[id]) {
					delete scenes[id]._meta?.new;
				}
				return { ...scenes };
			}),
		remove: (id) =>
			update((scenes) => {
				console.log('DELETE SCENE ', id);
				delete scenes[id];
				return scenes;
			}),
		addScene: (scene) =>
			update((scenes) => {
				if (typeof scene._id !== 'undefined') {
					let newId = scene._id.toString(); // id should be string. BE might return number
					scene._name = newId; // PHP-API legacy identifier. PHP-API bulk_update relies on it on REORDER
					scene._id = newId;
					scene.id = newId;

					return { ...scenes, [newId]: migrateFlowSceneIfNeeded(scene) };
				}
				return scenes;
			}),
		editScene: (editedScene) =>
			update((scenes) => {
				console.log('EDIT SCENE HTTP ', editedScene);
				if (!editedScene.id) editedScene.id = editedScene._id;
				scenes[editedScene._id] = migrateFlowSceneIfNeeded(editedScene);
				return scenes;
			}),
		bulkEditScenes: (editedScenes: {}) =>
			update((scenes) => {
				console.log('BULK SCENES UPDATE ', editedScenes);
				return { ...scenes, ...normalize_scene_ids(editedScenes as Record<string, any>) };
			}),
		reset: () => set({}),
	};
}

export const scenes: any = create_scenes();

scenes.subscribe((val) => {
	localStorage.setItem('scenes', JSON.stringify(stripFlowGraphForLocalCache(val)));
});
